// Fase 4 · Handlers de los eventos del hub `/hub/tpv`. Reutilizan los mismos
// reducers/thunks que los hubs legacy (mesas, pedidos, productos, reservas,
// lease, último cobro) para que el comportamiento sea idéntico.
//
// `getCtx()` devuelve { dispatch, getState, history, user, enqueueSnackbar,
// closeSnackbar } del hook que monta la conexión (siempre el valor actual).
import {
  receiveMesaFromHub,
  createMesaSuccess,
  deleteMesaSuccess,
  cleanMesaAndProducts,
  getMesas,
} from "src/redux/slices/mesas";
import { leaseEventReceived } from "src/redux/slices/mesaLeases";
import { receiveLastPayment } from "src/redux/slices/session";
import { addPedido, getOnlyOrders } from "src/redux/slices/order";
import {
  fetchComensales,
  SyncCart,
  getProducts,
  deleteProductSuccess,
  getFrequentProducts,
} from "src/redux/slices/product";
import { getCategories, getTags } from "src/redux/slices/categories";
import {
  upsertBookingLocal,
  removeBookingLocal,
  fetchBookingAlerts,
} from "src/redux/slices/bookings";
import { upsertTx } from "src/redux/slices/cashdrawer";
import { lastPaymentFromEvent } from "src/utils/mesaLeaseCore";
import { getDeviceId } from "src/utils/deviceId";
import { requestSelectedCheckout } from "src/utils/tpvResync";
import { noteMesaChanged } from "src/utils/mesaCheckoutLoader";
import { planProductHubRefresh } from "src/utils/tpvSyncCore";
import { isRecentLocalDinerSplitAction } from "src/utils/localCheckoutFetchTracker";
import { isEcho } from "./eventRouter";
import { PATH_APP } from "src/routes/paths";
import { postActionRoute } from "src/utils/postActionRoute";
import { leaveClosedMesa } from "./selectedMesaClosed";

import {
  mergeSlimMesa,
  productEventsFromEnvelope,
  shouldRefetchOrdersList,
  createCoalescedRunner,
} from "./envelopeMappers";

// Mismo filtro que carga la lista al montar el TPV grande (tpv/index.js).
export const ONLY_ORDERS_CONFIG = { OnlyOrders: false, Last24Hours: true };

const noop = () => {};

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

function formatBookingToastBody(b) {
  const name = b?.name || "Cliente";
  const pax = b?.number ? `${b.number} pax` : "";
  let when = "";
  try {
    const d = new Date(b?.date);
    if (!Number.isNaN(d.getTime())) {
      when = d.toLocaleString("es-ES", {
        weekday: "short",
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
  } catch (_) {}
  return [name, pax, when].filter(Boolean).join(" · ");
}

export function buildTpvEventHandlers(getCtx) {
  const selectedId = () => getCtx()?.getState?.()?.mesas?.selectedId;

  // Eco = evento provocado por ESTE dispositivo (originDeviceId del sobre).
  // Antes se usaba además una ventana de 2 s desde la última mutación local
  // sobre la mesa (isRecentLocalFetch), pero el heartbeat del lease la
  // renovaba cada 10 s y se perdían cambios de OTROS dispositivos (pedido QR,
  // cocina, otro camarero): el origen del sobre es exacto, la ventana no.
  const isOwnEvent = (env) => !!env && (env.echo === true || isEcho(env, getDeviceId()));

  const refreshIfSelected = (idMesa, env) => {
    // Antes de cualquier filtro: la cuenta guardada de una mesa cobrándose
    // deja de valer (ver mesaCheckoutLoader → takeHeldCheckout).
    noteMesaChanged(idMesa);
    if (idMesa == null || !sameId(idMesa, selectedId())) return;
    if (isOwnEvent(env)) return;
    requestSelectedCheckout(idMesa);
  };

  // Productos: misma ventana de 400 ms que el hub legacy.
  let productTimer = null;
  let productEvents = [];
  const flushProducts = () => {
    const { dispatch } = getCtx() || {};
    const plan = planProductHubRefresh(productEvents);
    productEvents = [];
    productTimer = null;
    if (!dispatch) return;
    plan.deletedIds.forEach((id) => dispatch(deleteProductSuccess(id)));
    if (plan.refetchProducts) dispatch(getProducts(undefined, { force: true }));
    if (plan.refetchCatalogExtras) {
      dispatch(getCategories(undefined, { force: true }));
      dispatch(getFrequentProducts());
      dispatch(getTags(false, { force: true }));
    }
  };

  // Lista de pedidos (onlyOrders): los eventos V2 son slim (sin productos),
  // así que se re-pide la lista agrupando ráfagas (una petición por 500 ms).
  const refetchOrders = createCoalescedRunner(() => {
    const { dispatch } = getCtx() || {};
    return dispatch?.(getOnlyOrders(ONLY_ORDERS_CONFIG, { force: true }));
  }, 500);
  const ordersListInUse = () => {
    const { getState, history } = getCtx() || {};
    const list = getState?.()?.order?.onlyOrders;
    if (Array.isArray(list) && list.length > 0) return true;
    const path = history?.location?.pathname || window.location.pathname || "";
    return path.startsWith(PATH_APP.tuCarta.tpv);
  };
  const onPedidoEvent = (type, data) => {
    const { getState } = getCtx() || {};
    const onlyOrders = getState?.()?.order?.onlyOrders;
    if (shouldRefetchOrdersList(type, data, { onlyOrders, listInUse: ordersListInUse() })) {
      refetchOrders();
    }
  };

  const onMesaChanged = (data, env) => {
      const { dispatch, getState, history, user } = getCtx() || {};
      if (!dispatch) return;
      const id = data?.id ?? env?.id;
      noteMesaChanged(id);
      const existing = (getState()?.mesas?.mesas || []).find((m) => sameId(m?.id, id));
      if (!existing) {
        // Mesa que no conocemos (alta en otro dispositivo): plano completo.
        dispatch(getMesas({ fresh: true }));
        return;
      }
      const merged = mergeSlimMesa(existing, { ...data, id: existing.id });
      const own = isOwnEvent(env);
      // Eco de una acción anterior (p. ej. la comanda) que llega mientras
      // este dispositivo cobra la mesa: no la reabre en el plano; el eco del
      // cobro llegará detrás con la mesa libre.
      const closing = getState()?.mesas?.isClosingMesa;
      if (own && merged.ocupada === true && sameId(closing, existing.id)) return;
      // La ficha del servidor manda SIEMPRE, también en el eco propio: es la
      // que corrige el estado optimista (comensales a 0 al cobrar, etc.).
      dispatch(receiveMesaFromHub(merged));

      // Eco propio: la acción ya hizo sus efectos (limpiar, navegar, refetch).
      if (own || !sameId(id, selectedId())) return;
      if (merged.ocupada === false) {
        // Cerrada desde OTRO dispositivo (o repuesta al volver del bolsillo):
        // salir de la mesa explicando por qué. La propia sigue igual que antes.
        const origin = env?.originDeviceId;
        if (origin && origin !== getDeviceId()) {
          leaveClosedMesa({ dispatch, getState, history, user, mesaId: existing.id });
          return;
        }
        dispatch(cleanMesaAndProducts());
        const currentPath = history?.location?.pathname || window.location.pathname;
        if (currentPath.startsWith(PATH_APP.tpv.cartaCamarero)) {
          history?.push(postActionRoute(user));
        }
      } else {
        requestSelectedCheckout(id);
      }
  };

  return {
    "mesa.changed": onMesaChanged,
    // KDS: cambio de estado de productos. Sustituye al par legacy `mesa/{rid}`
    // + `mesa/updateStateOfProducts/{rid}`; el Front solo usaba el primero
    // (no había listener de updateStateOfProducts), así que es el mismo merge.
    "mesa.productsUpdated": onMesaChanged,
    // Comensales por QR: lo consumen las pantallas StartSession (camarero y
    // cliente) con su propio hub `startsessionhub`, que sigue siendo legacy
    // (el servidor mantiene Realtime:LegacyHubs=true). Aquí no hay consumidor.
    "mesa.comensales": noop,
    "mesa.created": (data) => {
      const { dispatch } = getCtx() || {};
      if (data) dispatch?.(createMesaSuccess(data));
    },
    "mesa.deleted": (data, env) => {
      const { dispatch } = getCtx() || {};
      const id = env?.id ?? data?.id ?? data;
      if (id != null) dispatch?.(deleteMesaSuccess(id));
    },
    "mesas.reset": () => {
      const { dispatch } = getCtx() || {};
      dispatch?.(getMesas({ fresh: true }));
    },
    "session.changed": (data, env) => refreshIfSelected(data?.idMesa, env),
    "pedido.created": (data, env) => {
      const { dispatch } = getCtx() || {};
      const pedido = data?.pedido || data;
      if (dispatch && pedido && Array.isArray(pedido.productos)) {
        // DTO completo (por si el servidor lo envía): mismo reducer que el legacy.
        dispatch(addPedido({ ...pedido, id: pedido.id ?? env?.id }));
      } else {
        onPedidoEvent("pedido.created", { ...pedido, id: pedido?.id ?? env?.id });
      }
      refreshIfSelected(data?.idMesa ?? pedido?.idMesa, env);
    },
    "pedido.changed": (data, env) => {
      onPedidoEvent("pedido.changed", { ...data, id: data?.id ?? env?.id });
      refreshIfSelected(data?.idMesa, env);
    },
    "pedido.ready": (data, env) => refreshIfSelected(data?.idMesa, env),

    // Fase 1 · presencia y último cobro (nunca se descartan por eco).
    "lease.changed": (data) => {
      const { dispatch } = getCtx() || {};
      dispatch?.(leaseEventReceived({ evt: data, myDeviceId: getDeviceId() }));
    },
    "payment.recorded": (data) => {
      const { dispatch } = getCtx() || {};
      dispatch?.(receiveLastPayment(lastPaymentFromEvent(data, getDeviceId())));
    },

    "dinerSplit.changed": (data, env) => {
      const { dispatch, getState } = getCtx() || {};
      const idSession = env?.id ?? data?.idSession;
      if (!dispatch || !idSession) return;
      if (isRecentLocalDinerSplitAction(idSession)) return;
      dispatch(fetchComensales(idSession));
      const activeMesa = getState()?.mesas?.mesa;
      const currentSession = activeMesa?.sessionId || activeMesa?.idSession;
      if (sameId(currentSession, idSession) && activeMesa?.id) {
        dispatch(SyncCart(activeMesa.id, idSession));
      }
    },

    "products.changed": (data) => {
      productEvents.push(...productEventsFromEnvelope(data));
      if (!productTimer) productTimer = setTimeout(flushProducts, 400);
    },

    "booking.changed": (data, env) => {
      const { dispatch, enqueueSnackbar } = getCtx() || {};
      if (!dispatch) return;
      const action = data?.action;
      const booking = data?.booking || data;
      if (action === "deleted" || data?.deleted) {
        const id = env?.id ?? booking?.id;
        if (id != null) dispatch(removeBookingLocal(id));
        return;
      }
      if (!booking) return;
      dispatch(upsertBookingLocal(booking));
      dispatch(fetchBookingAlerts({ force: true }));
      if (!enqueueSnackbar) return;
      if (action === "created") {
        const pending = booking.status === 0;
        enqueueSnackbar(
          `${pending ? "Nueva reserva pendiente de confirmar" : "Nueva reserva"} — ${formatBookingToastBody(booking)}`,
          { variant: pending ? "warning" : "info" },
        );
      } else if (booking.status === 4) {
        enqueueSnackbar(`Reserva cancelada — ${formatBookingToastBody(booking)}`, { variant: "warning" });
      } else if (booking.status === 3) {
        enqueueSnackbar(`Reserva marcada como ausente — ${formatBookingToastBody(booking)}`, {
          variant: "warning",
        });
      }
    },

    "cashdrawer.updated": (data) => {
      const { dispatch } = getCtx() || {};
      if (data) dispatch?.(upsertTx(data));
    },

    // Siguen llegando por los hubs legacy que se mantienen (userhub,
    // usersession, notificationrestaurantehub, displaykitchenhub): aquí se
    // ignoran para no procesarlos dos veces.
    "kds.*": noop,
    "device.*": noop,
    "user.*": noop,
    "notification.*": noop,
  };
}
